package com.trevorwiebe.timesheet.admin.ownerOperator.presentation

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.admin.ownerOperator.domain.OwnerOperatorRepository
import com.trevorwiebe.timesheet.core.domain.model.OwnerOperator
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class OwnerOperatorsViewModel(
    private val ownerOperatorRepository: OwnerOperatorRepository
) : DisposableViewModel() {

    private val _state = MutableStateFlow(OwnerOperatorsState())
    val state = _state.asStateFlow()

    init {
        loadOwnerOperators()
    }

    fun onEvent(event: OwnerOperatorsEvent) {
        when (event) {
            is OwnerOperatorsEvent.OnAddOwnerOperatorClick -> {
                _state.update { it.copy(showAddEditDialog = true, editingOwnerOperator = null) }
            }
            is OwnerOperatorsEvent.OnEditOwnerOperatorClick -> {
                _state.update {
                    it.copy(showAddEditDialog = true, editingOwnerOperator = event.ownerOperator)
                }
            }
            is OwnerOperatorsEvent.OnDismissDialog -> {
                _state.update { it.copy(showAddEditDialog = false, editingOwnerOperator = null) }
            }
            is OwnerOperatorsEvent.OnSaveOwnerOperator -> {
                saveOwnerOperator(event)
            }
            is OwnerOperatorsEvent.OnDeleteOwnerOperator -> {
                deleteOwnerOperator()
            }
            is OwnerOperatorsEvent.OnRestoreOwnerOperator -> {
                restoreOwnerOperator()
            }
            is OwnerOperatorsEvent.OnFilterToggled -> {
                _state.update { state ->
                    val current = state.activeFilters
                    val updated = if (event.filter in current) {
                        current - event.filter
                    } else {
                        current + event.filter
                    }
                    state.copy(activeFilters = updated)
                }
            }
            is OwnerOperatorsEvent.OnErrorShown -> {
                _state.update { it.copy(error = null) }
            }
        }
    }

    @Suppress("UNCHECKED_CAST")
    private fun loadOwnerOperators() {
        viewModelScope.launch {
            _state.update { it.copy(isLoading = true) }
            val flow = ownerOperatorRepository.getOwnerOperators()
            flow.collect { result ->
                if (result.error != null) {
                    _state.update { it.copy(isLoading = false, error = result.error) }
                } else {
                    val ownerOperators = result.data as List<OwnerOperator>
                    _state.update {
                        it.copy(isLoading = false, ownerOperators = ownerOperators, error = null)
                    }
                }
            }
        }
    }

    private fun saveOwnerOperator(event: OwnerOperatorsEvent.OnSaveOwnerOperator) {
        viewModelScope.launch {
            val editing = _state.value.editingOwnerOperator
            val result = if (editing == null) {
                ownerOperatorRepository.addOwnerOperator(
                    OwnerOperator(
                        id = "",
                        contactName = event.contactName.trim(),
                        companyName = event.companyName.trim(),
                        address = event.address.trim(),
                        phone = event.phone.trim(),
                        email = event.email.trim(),
                        notes = event.notes.trim(),
                        basePayPercent = event.basePayPercent,
                        active = event.active,
                        deleted = false,
                        order = (_state.value.ownerOperators.maxOfOrNull { it.order } ?: -1) + 1
                    )
                )
            } else {
                ownerOperatorRepository.updateOwnerOperator(
                    editing.copy(
                        contactName = event.contactName.trim(),
                        companyName = event.companyName.trim(),
                        address = event.address.trim(),
                        phone = event.phone.trim(),
                        email = event.email.trim(),
                        notes = event.notes.trim(),
                        basePayPercent = event.basePayPercent,
                        active = event.active
                    )
                )
            }
            _state.update {
                it.copy(showAddEditDialog = false, editingOwnerOperator = null, error = result.error)
            }
        }
    }

    private fun deleteOwnerOperator() {
        viewModelScope.launch {
            val editing = _state.value.editingOwnerOperator ?: return@launch
            val result = ownerOperatorRepository.updateOwnerOperator(editing.copy(deleted = true))
            _state.update {
                it.copy(showAddEditDialog = false, editingOwnerOperator = null, error = result.error)
            }
        }
    }

    private fun restoreOwnerOperator() {
        viewModelScope.launch {
            val editing = _state.value.editingOwnerOperator ?: return@launch
            val result = ownerOperatorRepository.updateOwnerOperator(editing.copy(deleted = false))
            _state.update {
                it.copy(showAddEditDialog = false, editingOwnerOperator = null, error = result.error)
            }
        }
    }
}
